// File: frontend/src/pages/NegativeFeedback.js
// صفحة عرض الأسئلة والأجوبة التي تم تقييمها كخاطئة أو غير مفيدة
// Enhanced: سياق المحادثة + اقتراحات تحسين + تحليل

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiSearch,
  FiAlertCircle,
  FiThumbsDown,
  FiXCircle,
  FiX,
  FiFilter,
  FiCalendar,
  FiUser,
  FiMessageSquare,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiExternalLink,
  FiCheckCircle,
  FiBarChart2,
  FiTrendingUp,
  FiGlobe,
  FiPhone,
  FiHash,
  FiRefreshCw,
  FiChevronRight,
  FiEye,
  FiEdit3,
  FiStar,
  FiCpu,
  FiTag
} from 'react-icons/fi';
import Layout from '../components/Layout';
import './NegativeFeedback.css';

const NegativeFeedback = () => {
  const navigate = useNavigate();
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, wrong, not_helpful
  const [filterLanguage, setFilterLanguage] = useState('all'); // all, ar, en
  const [dateRange, setDateRange] = useState('all'); // all, today, week, month
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [stats, setStats] = useState({
    total: 0,
    wrong: 0,
    not_helpful: 0,
    today: 0,
    from_bot: 0
  });
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [dismissedBotIds, setDismissedBotIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissedBotAnswers') || '[]'); } catch { return []; }
  });

  const handleDismissBotAnswer = (id) => {
    const updated = [...dismissedBotIds, id];
    setDismissedBotIds(updated);
    localStorage.setItem('dismissedBotAnswers', JSON.stringify(updated));
    setBotAnswers(prev => prev.filter(item => item.id !== id));
  };

  // ============================================================
  // FETCH FEEDBACK DATA
  // ============================================================
  useEffect(() => {
    if (activeFilter === 'from_bot') {
      fetchBotAnswers();
    } else {
      fetchNegativeFeedback();
    }
  }, [filterType, filterLanguage, dateRange, activeFilter]);

  const [botAnswers, setBotAnswers] = useState([]);

  const fetchBotAnswers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch bot answers
      const response = await fetch(`/api/v1/feedback/bot-answers?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch bot answers');
      const data = await response.json();
      const allAnswers = data.answers || [];
      const filtered = allAnswers.filter(a => !dismissedBotIds.includes(a.id));
      setBotAnswers(filtered);
      
      // Update stats with correct bot count (minus dismissed)
      const totalBot = (data.total || allAnswers.length) - dismissedBotIds.length;
      setStats(prev => ({ ...prev, from_bot: Math.max(0, totalBot) }));
      
      // Also fetch regular stats
      const statsResponse = await fetch(`/api/v1/feedback/negative?limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData.stats) {
          setStats(prev => ({ 
            ...prev, 
            total: statsData.stats.total || 0,
            wrong: statsData.stats.wrong || 0,
            not_helpful: statsData.stats.not_helpful || 0,
            from_bot: data.total || data.answers?.length || 0
          }));
        }
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching bot answers:', err);
      setError('فشل تحميل بيانات إجابات البوت.');
    } finally {
      setLoading(false);
    }
  };

  const fetchNegativeFeedback = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (filterLanguage !== 'all') params.append('language', filterLanguage);
      if (dateRange !== 'all') params.append('dateRange', dateRange);

      const response = await fetch(`/api/v1/feedback/negative?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch feedback');
      }

      const data = await response.json();
      setFeedbackList(data.feedback || []);
      
      // Fetch real bot count from messages table
      const botCountRes = await fetch(`/api/v1/feedback/bot-answers?limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const botCountData = botCountRes.ok ? await botCountRes.json() : null;
      const realBotCount = Math.max(0, (botCountData?.total || data.stats?.from_bot || 0) - dismissedBotIds.length);
      
      setStats({ ...(data.stats || { total: 0, wrong: 0, not_helpful: 0 }), from_bot: realBotCount });
      setError(null);
    } catch (err) {
      console.error('Error fetching negative feedback:', err);
      setError('فشل تحميل البيانات. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // FETCH ANALYSIS DATA
  // ============================================================
  const fetchAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/feedback/analysis?period=30', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAnalysisData(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching analysis:', error);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const toggleAnalysis = () => {
    if (!showAnalysis && !analysisData) {
      fetchAnalysis();
    }
    setShowAnalysis(!showAnalysis);
  };

  // ============================================================
  // TOGGLE EXPAND
  // ============================================================
  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  // ============================================================
  // CLIENT-SIDE CSV EXPORT (preserved from old version)
  // ============================================================
  const exportToCSV = () => {
    const headers = ['التاريخ', 'نوع التقييم', 'السؤال', 'الإجابة', 'اللغة', 'رقم الهاتف'];
    const csvData = filteredFeedback.map(item => [
      new Date(item.created_at).toLocaleString('ar-EG'),
      item.feedback_type === 'wrong' ? 'خطأ' : 'غير مفيد',
      item.user_question || 'غير متوفر',
      item.bot_answer || 'غير متوفر',
      item.language === 'ar' ? 'عربي' : 'إنجليزي',
      item.phone_number || 'غير متوفر'
    ]);

    const csv = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `negative_feedback_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ============================================================
  // RESOLVE FEEDBACK
  // ============================================================
  const handleResolve = async (id, note, action) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/feedback/negative/${id}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolution_note: note, action_taken: action })
      });

      if (response.ok) {
        fetchNegativeFeedback(); // Refresh
      }
    } catch (error) {
      console.error('Error resolving:', error);
    }
  };

  // ============================================================
  // CLIENT-SIDE SEARCH FILTER (preserved from old version)
  // ============================================================
  const filteredFeedback = feedbackList.filter(item => {
    const matchesSearch = !searchTerm || 
      item.user_question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.bot_answer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.phone_number?.includes(searchTerm);
    
    let matchesFilter = true;
    if (activeFilter === 'wrong') {
      matchesFilter = item.feedback_type === 'wrong';
    } else if (activeFilter === 'not_helpful') {
      matchesFilter = item.feedback_type === 'not_helpful';
    } else if (activeFilter === 'from_bot') {
      matchesFilter = item.ai_source && !['elasticsearch', 'knowledge_base'].includes(item.ai_source);
    }
    
    return matchesSearch && matchesFilter;
  });

  // ============================================================
  // HELPERS (preserved from old version)
  // ============================================================
  const getFeedbackIcon = (type) => {
    if (type === 'wrong') {
      return <FiXCircle size={20} />;
    }
    return <FiThumbsDown size={20} />;
  };

  const getFeedbackLabel = (type) => {
    return type === 'wrong' ? 'إجابة خاطئة' : 'إجابة غير مفيدة';
  };

  const handleViewConversation = (conversationId) => {
    navigate('/my-conversations', { 
      state: { openConversationId: conversationId } 
    });
  };

  const handleImproveAnswer = (item) => {
    setImproveModal({
      open: true,
      item: item,
      esResults: [],
      searching: true,
      selectedMatch: null,
      newPhrasing: item.user_question || '',
      saving: false,
      saved: false,
      error: null
    });
    // Auto-search ES
    searchES(item.user_question || '');
  };

  const [improveModal, setImproveModal] = useState({ open: false });

  const searchES = async (query) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/feedback/es-search', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      const data = await response.json();
      setImproveModal(prev => ({ ...prev, esResults: data.results || [], searching: false }));
    } catch (err) {
      setImproveModal(prev => ({ ...prev, esResults: [], searching: false, error: 'ES search failed' }));
    }
  };

  const handleAddPhrasing = async () => {
    const { selectedMatch, newPhrasing } = improveModal;
    if (!selectedMatch || !newPhrasing) return;
    
    setImproveModal(prev => ({ ...prev, saving: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/feedback/add-phrasing', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: selectedMatch.question_id,
          es_id: selectedMatch.es_id,
          new_phrasing: newPhrasing.trim()
        })
      });
      const data = await response.json();
      if (data.success) {
        setImproveModal(prev => ({ ...prev, saving: false, saved: true, error: data.already_exists ? 'Phrasing already exists' : null }));
      } else {
        setImproveModal(prev => ({ ...prev, saving: false, error: data.message }));
      }
    } catch (err) {
      setImproveModal(prev => ({ ...prev, saving: false, error: 'Failed to add phrasing' }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
  };

  // ============================================================
  // LOADING STATE
  // ============================================================
  if (loading) {
    return (
      <Layout>
        <div className="negative-feedback-page">
          <div className="negative-loading-state">
            <div className="loading-spinner-large"></div>
            <p>جاري تحميل التقييمات السلبية...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <Layout>
      <div className="negative-feedback-page">

        {/* ===== Page Header ===== */}
        <div className="negative-feedback-header">
          <div className="header-icon-wrapper">
            <FiAlertCircle className="header-icon" />
          </div>
          <div className="header-content">
            <h1>التقييمات السلبية</h1>
            <p>مراجعة وتحليل الإجابات التي قيّمها العملاء بشكل سلبي مع سياق المحادثة</p>
          </div>
          <button className="analysis-toggle-btn" onClick={toggleAnalysis}>
            <FiBarChart2 size={18} />
            {showAnalysis ? 'إخفاء التحليل' : 'عرض التحليل'}
          </button>
        </div>

        {/* ===== Statistics Cards ===== */}
        <div className="negative-stats-grid">
          <div 
            className={`negative-stat-card wrong ${activeFilter === 'wrong' ? 'active' : ''}`}
            onClick={() => { setActiveFilter(activeFilter === 'wrong' ? 'all' : 'wrong'); setFilterType('all'); }}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon-circle">
              <FiXCircle size={24} />
            </div>
            <div className="stat-details">
              <div className="stat-value">{stats.wrong}</div>
              <div className="stat-label">إجابات خاطئة</div>
            </div>
          </div>

          <div 
            className={`negative-stat-card not-helpful ${activeFilter === 'not_helpful' ? 'active' : ''}`}
            onClick={() => { setActiveFilter(activeFilter === 'not_helpful' ? 'all' : 'not_helpful'); setFilterType('all'); }}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon-circle">
              <FiThumbsDown size={24} />
            </div>
            <div className="stat-details">
              <div className="stat-value">{stats.not_helpful}</div>
              <div className="stat-label">إجابات غير مفيدة</div>
            </div>
          </div>

          <div 
            className={`negative-stat-card from-bot ${activeFilter === 'from_bot' ? 'active' : ''}`}
            onClick={() => { setActiveFilter(activeFilter === 'from_bot' ? 'all' : 'from_bot'); setFilterType('all'); }}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon-circle">
              <FiCpu size={24} />
            </div>
            <div className="stat-details">
              <div className="stat-value">{stats.from_bot}</div>
              <div className="stat-label">إجابات من البوت</div>
            </div>
          </div>

          <div 
            className={`negative-stat-card total ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => { setActiveFilter('all'); setFilterType('all'); }}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon-circle">
              <FiAlertCircle size={24} />
            </div>
            <div className="stat-details">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">الكل</div>
            </div>
          </div>
        </div>

        {/* ===== Analysis Panel (NEW) ===== */}
        {showAnalysis && (
          <div className="analysis-panel">
            {analysisLoading ? (
              <div className="analysis-loading">
                <FiRefreshCw size={24} className="spinning" />
                <span>جاري تحليل البيانات...</span>
              </div>
            ) : analysisData ? (
              <>
                {/* Satisfaction Rate */}
                <div className="analysis-section">
                  <h3><FiTrendingUp size={18} /> معدل رضا العملاء (آخر 30 يوم)</h3>
                  <div className="satisfaction-bar-container">
                    <div className="satisfaction-bar">
                      <div
                        className="satisfaction-fill"
                        style={{ width: `${analysisData.satisfactionRate?.satisfaction_percent || 0}%` }}
                      />
                    </div>
                    <span className="satisfaction-value">
                      {analysisData.satisfactionRate?.satisfaction_percent || 0}%
                    </span>
                  </div>
                  <p className="analysis-note">
                    من أصل {analysisData.satisfactionRate?.total || 0} تقييم،{' '}
                    {analysisData.satisfactionRate?.positive || 0} تقييم إيجابي
                  </p>
                </div>

                {/* Pattern Analysis */}
                {analysisData.patternAnalysis && analysisData.patternAnalysis.length > 0 && (
                  <div className="analysis-section">
                    <h3><FiStar size={18} /> أنماط الأسئلة الأكثر تسبباً في مشاكل</h3>
                    <div className="pattern-grid">
                      {analysisData.patternAnalysis.map((pattern, idx) => (
                        <div key={idx} className="pattern-chip">
                          <span className="pattern-name">{pattern.question_pattern}</span>
                          <span className="pattern-count">{pattern.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Problems */}
                {analysisData.topProblems && analysisData.topProblems.length > 0 && (
                  <div className="analysis-section">
                    <h3><FiAlertCircle size={18} /> أكثر الأسئلة المُبلّغ عنها</h3>
                    <div className="top-problems-list">
                      {analysisData.topProblems.slice(0, 5).map((problem, idx) => (
                        <div key={idx} className="problem-item">
                          <div className="problem-rank">#{idx + 1}</div>
                          <div className="problem-content">
                            <div className="problem-question">{problem.user_question}</div>
                            <div className="problem-meta">
                              <span className={`problem-type ${problem.feedback_type}`}>
                                {problem.feedback_type === 'wrong' ? 'خطأ' : 'غير مفيد'}
                              </span>
                              <span className="problem-count">{problem.report_count} بلاغ</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="analysis-loading">
                <p>لا توجد بيانات كافية للتحليل</p>
              </div>
            )}
          </div>
        )}

        {/* ===== Filters and Search ===== */}
        <div className="negative-filters-section">
          <div className="negative-filters-row">
            <div className="negative-search-wrapper">
              <FiSearch className="search-icon" size={18} />
              <input
                type="text"
                placeholder="ابحث في الأسئلة أو الأجوبة أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="negative-search-input"
              />
            </div>

            <div className="negative-filters-group">
              <div className="negative-filter-item">
                <FiFilter size={18} />
                <select 
                  value={filterType} 
                  onChange={(e) => { setFilterType(e.target.value); setActiveFilter('all'); }}
                  className="negative-filter-select"
                >
                  <option value="all">جميع الأنواع</option>
                  <option value="wrong">إجابات خاطئة</option>
                  <option value="not_helpful">إجابات غير مفيدة</option>
                </select>
              </div>

              <div className="negative-filter-item">
                <FiMessageSquare size={18} />
                <select 
                  value={filterLanguage} 
                  onChange={(e) => setFilterLanguage(e.target.value)}
                  className="negative-filter-select"
                >
                  <option value="all">جميع اللغات</option>
                  <option value="ar">عربي</option>
                  <option value="en">English</option>
                </select>
              </div>

              <div className="negative-filter-item">
                <FiCalendar size={18} />
                <select 
                  value={dateRange} 
                  onChange={(e) => setDateRange(e.target.value)}
                  className="negative-filter-select"
                >
                  <option value="all">كل الفترات</option>
                  <option value="today">اليوم</option>
                  <option value="week">هذا الأسبوع</option>
                  <option value="month">هذا الشهر</option>
                </select>
              </div>

              <button 
                className="negative-btn-export"
                onClick={exportToCSV}
                disabled={filteredFeedback.length === 0}
              >
                <FiDownload size={18} />
                تصدير CSV
              </button>
            </div>
          </div>
        </div>

        {/* ===== Error Alert ===== */}
        {error && (
          <div className="negative-alert error">
            <FiAlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* ===== Feedback List ===== */}
        <div className="negative-feedback-list">
          {activeFilter === 'from_bot' ? (
            /* ===== Bot Answers View ===== */
            botAnswers.length === 0 ? (
              <div className="negative-empty-state">
                <div className="empty-state-icon"><FiCpu size={48} /></div>
                <h3>لا توجد إجابات من البوت</h3>
                <p>لم يتم العثور على إجابات مولّدة من الذكاء الاصطناعي</p>
              </div>
            ) : (
              botAnswers.filter(item => {
                if (!searchTerm) return true;
                return item.user_question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  item.bot_answer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  item.phone_number?.includes(searchTerm);
              }).map((item) => (
                <div key={item.id} className="negative-feedback-card bot-generated">
                  {/* Card Header */}
                  <div className="feedback-card-header">
                    <div className="feedback-header-left">
                      <div className="feedback-icon-large bot">
                        <FiCpu size={20} />
                      </div>
                      <div className="feedback-title-section">
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                          <div className="feedback-type-badge bot">
                            إجابة من البوت ({item.ai_source})
                          </div>
                          {item.feedback_type && (
                            <span className={`feedback-mini-badge ${item.feedback_type}`}>
                              {item.feedback_type === 'wrong' ? '❌ خاطئة' : item.feedback_type === 'not_helpful' ? '👎 غير مفيدة' : ''}
                            </span>
                          )}
                        </div>
                        <div className="feedback-timestamp">
                          <FiClock size={14} />
                          {formatDate(item.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Meta Row - same structure as original */}
                  <div className="feedback-meta-row">
                    <div className="feedback-meta-item">
                      <FiUser size={16} />
                      <span>{item.phone_number || 'غير معروف'}</span>
                    </div>
                    <div className="feedback-meta-item">
                      <FiMessageSquare size={16} />
                      <span>{item.language === 'ar' ? 'عربي' : 'English'}</span>
                    </div>
                    <div className="feedback-meta-item">
                      <span className="conversation-id-badge">#{item.conversation_id}</span>
                    </div>
                    {item.ai_confidence && (
                      <div className="feedback-meta-item">
                        <FiBarChart2 size={16} />
                        <span>ثقة: {(item.ai_confidence * 100).toFixed(0)}%</span>
                      </div>
                    )}
                  </div>

                  {/* Main Content */}
                  <div className="feedback-main-content">
                    {/* Question */}
                    {item.user_question && (
                      <div className="feedback-section question">
                        <label className="feedback-section-label">سؤال العميل:</label>
                        <div className="feedback-text-content">
                          {item.user_question}
                        </div>
                      </div>
                    )}

                    {/* Answer */}
                    <div className="feedback-section answer">
                      <label className="feedback-section-label">إجابة البوت:</label>
                      <div className="feedback-text-content">
                        {item.bot_answer}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="feedback-actions-row">
                      <button 
                        className="feedback-action-btn primary"
                        onClick={() => handleViewConversation(item.conversation_id)}
                      >
                        <FiExternalLink size={16} />
                        View Conversation
                      </button>
                      <button 
                        className="feedback-action-btn secondary"
                        onClick={() => handleImproveAnswer({ user_question: item.user_question, bot_answer: item.bot_answer, conversation_id: item.conversation_id })}
                      >
                        <FiEdit3 size={16} />
                        Improve Answer
                      </button>
                      <button 
                        className="feedback-action-btn dismiss-btn"
                        onClick={() => handleDismissBotAnswer(item.id)}
                      >
                        <FiXCircle size={16} />
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )
          ) : (
          filteredFeedback.length === 0 ? (
            <div className="negative-empty-state">
              <div className="empty-state-icon">
                <FiAlertCircle size={48} />
              </div>
              <h3>لا توجد تقييمات سلبية</h3>
              <p>
                {searchTerm 
                  ? 'لم يتم العثور على نتائج مطابقة لبحثك'
                  : 'لا توجد تقييمات سلبية في الفترة المحددة'}
              </p>
            </div>
          ) : (
            filteredFeedback.map((item) => {
              const isExpanded = expandedItems.has(item.id);
              
              return (
                <div key={item.id} className={`negative-feedback-card ${item.feedback_type === 'wrong' ? 'wrong' : 'not-helpful'}`}>
                  {/* Card Header */}
                  <div className="feedback-card-header">
                    <div className="feedback-header-left">
                      <div className={`feedback-icon-large ${item.feedback_type}`}>
                        {getFeedbackIcon(item.feedback_type)}
                      </div>
                      <div className="feedback-title-section">
                        <div className={`feedback-type-badge ${item.feedback_type}`}>
                          {getFeedbackLabel(item.feedback_type)}
                        </div>
                        <div className="feedback-timestamp">
                          <FiClock size={14} />
                          {formatDate(item.created_at)}
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      className="feedback-expand-btn"
                      onClick={() => toggleExpand(item.id)}
                    >
                      {isExpanded ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                    </button>
                  </div>

                  {/* Meta Row */}
                  <div className="feedback-meta-row">
                    <div className="feedback-meta-item">
                      <FiUser size={16} />
                      <span>{item.phone_number || 'غير متوفر'}</span>
                    </div>
                    <div className="feedback-meta-item">
                      <FiMessageSquare size={16} />
                      <span>{item.language === 'ar' ? 'عربي' : 'English'}</span>
                    </div>
                    <div className="feedback-meta-item">
                      <span className="conversation-id-badge">#{item.conversation_id}</span>
                    </div>
                  </div>

                  {/* Main Content - Question always visible */}
                  <div className="feedback-main-content">
                    <div className="feedback-section question">
                      <label className="feedback-section-label">سؤال العميل:</label>
                      <div className="feedback-text-content">
                        {item.user_question || 'السؤال غير متوفر'}
                      </div>
                    </div>

                    {isExpanded && (
                      <>
                        {/* ===== NEW: Conversation Context (last 3 Q&A before issue) ===== */}
                        {item.conversation_context && item.conversation_context.length > 0 && (
                          <div className="context-section">
                            <div className="context-header">
                              <FiMessageSquare size={16} />
                              <span>سياق المحادثة (آخر {item.conversation_context.length} أسئلة قبل المشكلة)</span>
                            </div>
                            <div className="context-timeline">
                              {item.conversation_context.map((ctx, idx) => (
                                <div key={idx} className="context-qa-pair">
                                  <div className="context-message customer">
                                    <div className="context-msg-header">
                                      <FiUser size={14} />
                                      <span>العميل</span>
                                      <span className="context-time">{formatTime(ctx.timestamp)}</span>
                                    </div>
                                    <div className="context-msg-text">{ctx.question}</div>
                                  </div>
                                  <div className="context-message bot">
                                    <div className="context-msg-header">
                                      <FiCpu size={14} />
                                      <span>البوت</span>
                                    </div>
                                    <div className="context-msg-text">{ctx.answer}</div>
                                  </div>
                                  {idx < item.conversation_context.length - 1 && (
                                    <div className="context-separator">
                                      <FiChevronRight size={14} />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Bot Answer (original - preserved) */}
                        <div className="feedback-section answer">
                          <label className="feedback-section-label">إجابة البوت:</label>
                          <div className="feedback-text-content">
                            {item.bot_answer || 'الإجابة غير متوفرة'}
                          </div>
                        </div>

                        {/* Sources (preserved from old version) */}
                        {item.sources && (
                          <div className="feedback-section">
                            <label className="feedback-section-label">المصادر:</label>
                            <div className="feedback-sources">
                              {(() => {
                                try {
                                  const parsedSources = typeof item.sources === 'string' 
                                    ? JSON.parse(item.sources) 
                                    : item.sources;
                                  return parsedSources.map((source, idx) => (
                                    <div key={idx} className="feedback-source-item">
                                      <span className="source-item-label">مصدر {idx + 1}:</span>
                                      <span className="source-item-text">{source}</span>
                                    </div>
                                  ));
                                } catch (e) {
                                  return null;
                                }
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Additional Comment (preserved from old version) */}
                        {item.additional_comment && (
                          <div className="feedback-section comment">
                            <label className="feedback-section-label">تعليق إضافي:</label>
                            <div className="feedback-text-content">
                              {item.additional_comment}
                            </div>
                          </div>
                        )}

                        {/* ===== NEW: AI Suggestions ===== */}
                        {item.ai_suggestion && item.ai_suggestion.length > 0 && (
                          <div className="suggestions-section">
                            <div className="suggestions-header">
                              <FiStar size={16} />
                              <span>اقتراحات لتحسين البيانات</span>
                            </div>
                            <div className="suggestions-list">
                              {item.ai_suggestion.map((suggestion, idx) => (
                                <div key={idx} className={`suggestion-card ${suggestion.type}`}>
                                  <div className="suggestion-icon">{suggestion.icon}</div>
                                  <div className="suggestion-content">
                                    <div className="suggestion-title">{suggestion.title_ar}</div>
                                    <div className="suggestion-desc">{suggestion.desc_ar}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="feedback-actions-row">
                          <button 
                            className="feedback-action-btn primary"
                            onClick={() => handleViewConversation(item.conversation_id)}
                          >
                            <FiExternalLink size={16} />
                            عرض المحادثة الكاملة
                          </button>
                          <button 
                            className="feedback-action-btn secondary"
                            onClick={() => handleImproveAnswer(item)}
                          >
                            <FiEdit3 size={16} />
                            تحسين الإجابة
                          </button>

                          {/* NEW: Resolve button */}
                          {!item.resolved ? (
                            <ResolveButton 
                              itemId={item.id} 
                              onResolve={handleResolve} 
                            />
                          ) : (
                            <span className="resolved-badge">
                              <FiCheckCircle size={14} />
                              تم المعالجة
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )
          )}
        </div>
      </div>

      {/* ===== Improve Answer Modal ===== */}
      {improveModal.open && (
        <div className="improve-modal-overlay" onClick={() => setImproveModal({ open: false })}>
          <div className="improve-modal" onClick={e => e.stopPropagation()}>
            <div className="improve-modal-header">
              <h3><FiEdit3 size={20} /> Improve Answer</h3>
              <button className="improve-modal-close" onClick={() => setImproveModal({ open: false })}>
                <FiX size={20} />
              </button>
            </div>

            <div className="improve-modal-body">
              {/* Customer Question */}
              <div className="improve-section">
                <label className="improve-label">Customer Question:</label>
                <div className="improve-question-box">{improveModal.item?.user_question || 'N/A'}</div>
              </div>

              {/* Bot Answer */}
              <div className="improve-section">
                <label className="improve-label">Bot Answer:</label>
                <div className="improve-answer-box">{improveModal.item?.bot_answer || 'N/A'}</div>
              </div>

              {/* ES Search Results */}
              <div className="improve-section">
                <label className="improve-label">
                  <FiSearch size={16} /> Similar Questions in Knowledge Base:
                </label>
                {improveModal.searching ? (
                  <div className="improve-loading">Searching...</div>
                ) : improveModal.esResults.length === 0 ? (
                  <div className="improve-no-results">No similar questions found in knowledge base</div>
                ) : (
                  <div className="improve-results-list">
                    {improveModal.esResults.map((result, idx) => (
                      <div 
                        key={idx} 
                        className={`improve-result-card ${improveModal.selectedMatch?.es_id === result.es_id ? 'selected' : ''}`}
                        onClick={() => setImproveModal(prev => ({ ...prev, selectedMatch: result }))}
                      >
                        <div className="improve-result-header">
                          <span className="improve-result-score">Score: {result.score?.toFixed(1)}</span>
                          <span className="improve-result-id">Q#{result.question_id}</span>
                        </div>
                        <div className="improve-result-question">{result.question}</div>
                        <div className="improve-result-answer">{result.answer?.substring(0, 150)}...</div>
                        {result.phrasings && (
                          <div className="improve-result-phrasings">
                            <FiTag size={12} /> {result.phrasings.split('|').slice(0, 3).join(' | ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Phrasing */}
              {improveModal.selectedMatch && (
                <div className="improve-section improve-add-section">
                  <label className="improve-label">
                    Add as new phrasing to Q#{improveModal.selectedMatch.question_id}:
                  </label>
                  <div className="improve-phrasing-input-row">
                    <input
                      type="text"
                      className="improve-phrasing-input"
                      value={improveModal.newPhrasing}
                      onChange={e => setImproveModal(prev => ({ ...prev, newPhrasing: e.target.value }))}
                      placeholder="Edit phrasing if needed..."
                    />
                    <button 
                      className="improve-save-btn"
                      onClick={handleAddPhrasing}
                      disabled={improveModal.saving || improveModal.saved}
                    >
                      {improveModal.saving ? 'Saving...' : improveModal.saved ? '✓ Saved' : 'Add Phrasing'}
                    </button>
                  </div>
                  {improveModal.error && (
                    <div className="improve-error">{improveModal.error}</div>
                  )}
                  {improveModal.saved && !improveModal.error && (
                    <div className="improve-success">✓ Phrasing added! Bot will now recognize this question.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

// ============================================================
// RESOLVE BUTTON COMPONENT (NEW)
// ============================================================
const ResolveButton = ({ itemId, onResolve }) => {
  const [showForm, setShowForm] = useState(false);
  const [resolveNote, setResolveNote] = useState('');

  if (!showForm) {
    return (
      <button 
        className="feedback-action-btn resolve-btn"
        onClick={() => setShowForm(true)}
      >
        <FiCheckCircle size={16} />
        تم المعالجة
      </button>
    );
  }

  return (
    <div className="resolve-form">
      <textarea
        className="resolve-textarea"
        placeholder="اكتب ملاحظة عن الإجراء المتخذ (اختياري)..."
        value={resolveNote}
        onChange={(e) => setResolveNote(e.target.value)}
        rows={2}
      />
      <div className="resolve-actions">
        <button
          className="resolve-action-chip"
          onClick={() => { onResolve(itemId, resolveNote, 'answer_updated'); setShowForm(false); }}
        >
          تم تحديث الإجابة
        </button>
        <button
          className="resolve-action-chip"
          onClick={() => { onResolve(itemId, resolveNote, 'phrasing_added'); setShowForm(false); }}
        >
          تم إضافة صياغات
        </button>
        <button
          className="resolve-action-chip"
          onClick={() => { onResolve(itemId, resolveNote, 'question_added'); setShowForm(false); }}
        >
          تم إضافة سؤال جديد
        </button>
        <button
          className="resolve-action-chip"
          onClick={() => { onResolve(itemId, resolveNote, 'no_action'); setShowForm(false); }}
        >
          لا يحتاج إجراء
        </button>
        <button
          className="resolve-action-chip cancel"
          onClick={() => setShowForm(false)}
        >
          إلغاء
        </button>
      </div>
    </div>
  );
};

export default NegativeFeedback;